import { ChevronDown, ChevronUp, X } from 'lucide-react';
import { Badge, Button, Input } from '@platform/design-system';
import { useId, useState } from 'react';
import { CarrierStatus } from '../orders/CarrierStatus.tsx';
import { usePageContext } from '../usePageContext.ts';
import { damagedOf, isValidDamage, type BasketEntry, type DamagedCounts } from './returns.ts';
import styles from './Returns.module.css';

export type ReturnBasketRowProps = {
  entry: BasketEntry;
  carrierName: string;
  onCountsChange: (counts: DamagedCounts) => void;
  onRemove: () => void;
};

export const ReturnBasketRow = ({ entry, carrierName, onCountsChange, onRemove }: ReturnBasketRowProps) => {
  const { t } = usePageContext();
  const [open, setOpen] = useState(false);
  const detailsId = useId();
  const { order } = entry;
  const articles = order.lines.reduce((total, line) => total + line.quantity, 0);
  const damagedTotal = order.lines.reduce(
    (total, line) => total + damagedOf(entry.counts, line.variantId),
    0,
  );

  return (
    <li className={styles['entry']} aria-label={t('returns.parcel', { number: order.number })}>
      <div className={styles['entryHead']}>
        <span className={styles['stacked']}>
          <strong>{order.number}</strong>
          <span className={styles['muted']}>
            {order.customer.name} · {t('returns.articles', { count: articles })} · {carrierName}
          </span>
        </span>
        <CarrierStatus dispatch={order.dispatch} />
        {entry.announced ? null : (
          <Badge tone="warning">{t('returns.notAnnounced', { carrier: carrierName })}</Badge>
        )}
        <span className={styles['entryActions']}>
          <Button
            variant="ghost"
            size="sm"
            aria-expanded={open}
            aria-controls={detailsId}
            icon={
              open ? <ChevronUp size={16} aria-hidden="true" /> : <ChevronDown size={16} aria-hidden="true" />
            }
            onClick={() => {
              setOpen(!open);
            }}
          >
            {damagedTotal === 0
              ? t('returns.markDamaged')
              : t('returns.damagedCount', { count: damagedTotal })}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            icon={<X size={16} aria-hidden="true" />}
            aria-label={t('returns.remove', { number: order.number })}
            onClick={onRemove}
          />
        </span>
      </div>
      {entry.refusal === null ? null : (
        <p role="alert" className={styles['refusal']}>
          {entry.refusal}
        </p>
      )}
      {open ? (
        <ul id={detailsId} className={styles['lines']}>
          {order.lines.map((line) => {
            const damaged = damagedOf(entry.counts, line.variantId);
            const valid = isValidDamage(damaged, line.quantity);
            return (
              <li key={line.variantId} className={styles['line']}>
                <span>
                  {line.productName} <span className={styles['muted']}>{line.sku}</span>
                </span>
                <span className={styles['quantity']}>{t('returns.quantity', { count: line.quantity })}</span>
                <Input
                  label={t('returns.damaged')}
                  type="number"
                  inputMode="numeric"
                  min={0}
                  max={line.quantity}
                  value={String(damaged)}
                  errorText={valid ? undefined : t('returns.damagedInvalid', { max: line.quantity })}
                  onChange={(event) => {
                    onCountsChange({ ...entry.counts, [line.variantId]: event.target.valueAsNumber });
                  }}
                />
                <span className={styles['quantity']}>
                  {t('returns.restock', { count: valid ? line.quantity - damaged : 0 })}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </li>
  );
};
